/**
 * Cross-tab authentication event broadcasting utility
 * Uses BroadcastChannel API with localStorage fallback for older browsers
 */

const CHANNEL_NAME = 'auth-events';
const STORAGE_KEY = 'auth:broadcast';

export interface AuthBroadcastEvent {
  type: 'logged-in' | 'logged-out' | 'session-expired' | 'token-refreshed' | 'session-warning' | 'warning-dismissed';
  payload?: any;
  timestamp: number;
  tabId: string;
}

// Generate unique tab ID for this session
const TAB_ID = `tab-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;

/**
 * Broadcast an authentication event to all tabs
 */
export const broadcastAuthEvent = (type: AuthBroadcastEvent['type'], payload?: any): void => {
  const event: AuthBroadcastEvent = {
    type,
    payload,
    timestamp: Date.now(),
    tabId: TAB_ID
  };

  console.log(`📡 Broadcasting auth event: ${type}`, event);

  // Try BroadcastChannel first (modern browsers)
  try {
    const channel = new BroadcastChannel(CHANNEL_NAME);
    channel.postMessage(event);
    channel.close();
  } catch (error) {
    // Fallback to localStorage for older browsers
    console.log('📡 BroadcastChannel not available, using localStorage fallback');
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(event));
      // Clear the storage key after a short delay to trigger storage event
      setTimeout(() => {
        try {
          localStorage.removeItem(STORAGE_KEY);
        } catch {}
      }, 100);
    } catch (storageError) {
      console.warn('📡 Failed to broadcast auth event:', storageError);
    }
  }
};

/**
 * Listen for authentication events from other tabs
 */
export const onAuthBroadcast = (callback: (event: AuthBroadcastEvent) => void): (() => void) => {
  let broadcastChannel: BroadcastChannel | null = null;
  let storageHandler: ((e: StorageEvent) => void) | null = null;

  // Try BroadcastChannel first
  try {
    broadcastChannel = new BroadcastChannel(CHANNEL_NAME);
    broadcastChannel.onmessage = (e) => {
      const event = e.data as AuthBroadcastEvent;
      // Ignore events from this tab
      if (event.tabId !== TAB_ID) {
        console.log(`📡 Received auth event from another tab: ${event.type}`, event);
        callback(event);
      }
    };
  } catch (error) {
    console.log('📡 BroadcastChannel not available, using localStorage listener');
  }

  // Always set up localStorage fallback listener
  storageHandler = (e: StorageEvent) => {
    if (e.key === STORAGE_KEY && e.newValue) {
      try {
        const event = JSON.parse(e.newValue) as AuthBroadcastEvent;
        // Ignore events from this tab
        if (event.tabId !== TAB_ID) {
          console.log(`📡 Received auth event via localStorage: ${event.type}`, event);
          callback(event);
        }
      } catch (parseError) {
        console.warn('📡 Failed to parse auth broadcast event:', parseError);
      }
    }
  };

  window.addEventListener('storage', storageHandler);

  // Return cleanup function
  return () => {
    if (broadcastChannel) {
      broadcastChannel.close();
    }
    if (storageHandler) {
      window.removeEventListener('storage', storageHandler);
    }
  };
};

/**
 * Broadcast session warning event to all tabs
 * @param remainingSeconds - Number of seconds remaining until session expires
 */
export const broadcastSessionWarning = (remainingSeconds: number): void => {
  broadcastAuthEvent('session-warning', { remainingSeconds });
};

/**
 * Broadcast warning dismissed event to all tabs
 * This is used when user clicks "Stay Logged In" to dismiss warning on all tabs
 */
export const broadcastWarningDismissed = (): void => {
  broadcastAuthEvent('warning-dismissed');
};

/**
 * Get the current tab ID
 */
export const getCurrentTabId = (): string => TAB_ID;
